import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import * as CampaignContentAPI from "../../services/api/CampaignContent";
import { TestRecipient } from "../../services/api/CampaignContent";
import { MarketingCampaignDoc } from "@pollyslack/hub/src/types";

interface Props {
  campaign: MarketingCampaignDoc;
  isOpen: boolean;
  onClose: () => void;
}

export const TestDMModal: React.FC<Props> = ({ campaign, isOpen, onClose }) => {
  const [mongoId, setMongoId] = useState("");
  const [recipient, setRecipient] = useState<TestRecipient | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [sending, setSending] = useState(false);

  function reset() {
    setMongoId("");
    setRecipient(null);
    setLookupError(null);
    setLooking(false);
    setSending(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleLookup() {
    const trimmed = mongoId.trim();
    if (!trimmed) return;
    setLooking(true);
    setRecipient(null);
    setLookupError(null);
    try {
      const r = await CampaignContentAPI.getTestRecipient(trimmed);
      setRecipient(r);
    } catch (err: any) {
      setLookupError(err?.message || "Failed to look up user");
    } finally {
      setLooking(false);
    }
  }

  async function handleSend() {
    if (!recipient) return;
    setSending(true);
    try {
      const result = await CampaignContentAPI.testCampaign(
        campaign._id,
        recipient.mongoId,
      );
      // Defense in depth: rest.ts's post() throws on any non-2xx, so this
      // shouldn't trigger in practice, but never treat an error payload as
      // success if it somehow resolves instead of throwing (PS-5699).
      if (result.error) {
        throw new Error(result.message || result.error);
      }
      toast.success(
        `Test DM sent to ${recipient.realName || recipient.mongoId}.`,
      );
      handleClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to send test DM");
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        Send Test DM — {campaign.identifier}
      </ModalHeader>
      <ModalBody>
        <div className="form-group">
          <label className="font-weight-bold">Recipient User Mongo ID</label>
          <div className="d-flex">
            <input
              className="form-control mr-2"
              type="text"
              value={mongoId}
              onChange={(e) => {
                setMongoId(e.target.value);
                setRecipient(null);
                setLookupError(null);
              }}
              placeholder="Enter user mongo ID"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleLookup();
                }
              }}
            />
            <button
              className="btn btn-outline-primary"
              onClick={handleLookup}
              disabled={!mongoId.trim() || looking || sending}
              style={{ whiteSpace: "nowrap" }}
            >
              {looking && (
                <span
                  className="spinner-border spinner-border-sm mr-1"
                  role="status"
                />
              )}
              Look up
            </button>
          </div>
          <small className="form-text text-muted">
            Sends a real DM to this user via their own org's Slack bot — same
            delivery path as enrollment, just immediate.
          </small>
        </div>

        {lookupError && (
          <div className="alert alert-danger py-2 mb-2" role="alert">
            {lookupError}
          </div>
        )}

        {recipient && (
          <div className="alert alert-info py-2 mb-2" role="alert">
            <div>
              <strong>Recipient:</strong>{" "}
              {recipient.realName || recipient.displayName || "(no name)"}
            </div>
            {recipient.email && (
              <div>
                <strong>Email:</strong> {recipient.email}
              </div>
            )}
            <div>
              <strong>Mongo ID:</strong> <code>{recipient.mongoId}</code>
            </div>
            <div>
              <strong>Org ID:</strong> <code>{recipient.orgId}</code>
            </div>
            <div>
              <strong>Slack ID:</strong> <code>{recipient.slackUserId}</code>
            </div>
            <small className="d-block mt-2 text-muted">
              This will send a real DM in this user's workspace. CTAs are live —
              some buttons (e.g. Start Trial) trigger real flows on their org.
            </small>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-link"
          onClick={handleClose}
          disabled={sending}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={!recipient || sending}
        >
          {sending && (
            <span
              className="spinner-border spinner-border-sm mr-1"
              role="status"
            />
          )}
          Send Test DM
        </button>
      </ModalFooter>
    </Modal>
  );
};
